// @noflow
import { useReactiveVar } from '@apollo/client'
import upperFirst from 'lodash/upperFirst'
import React, { Fragment, useCallback } from 'react'

import { dogsDataVar } from '@/services/apollo'

import BREAKPOINTS from '@/constants/Breakpoints'

import useWindowSize from '@/hooks/useWindowSize'

import { SectionWrapper } from '@/components/elements/atoms/SectionWrapper'
import SkeletonParagraph from '@/components/elements/atoms/SkeletonParagraph/SkeletonParagraph'
import Text from '@/components/elements/atoms/Text/Text'
import CalorieBar from '@/components/elements/molecules/CalorieBar/CalorieBar'
import { OptionSelector } from '@/components/elements/molecules/OptionSelector'

import STYLES from './DailyCalorieGuidance.module.sass'

import type {
  EditDailyAmountQuery_user_dogs as Dog,
  EditDailyAmountQuery_user_subscription_plan as Plan
} from '../../queries/__generated__/EditDailyAmountQuery'

import type { DailyAmountOption } from '../../utils'
import { LargestPlanNotification } from '../LargestPlanNotification'
import { MultipleDogsCallout } from '../MultipleDogsCallout'

type Props = {
  dailyAmountOptions: Array<DailyAmountOption>
  currentPlan: Plan | null
  chosenPlan: Plan | null
  dogs: Array<Dog>
  setChosenPlanId: React.Dispatch<React.SetStateAction<string | null>>
  loading?: boolean
}

const DailyCalorieGuidance = ({
  dailyAmountOptions,
  currentPlan,
  chosenPlan,
  dogs,
  setChosenPlanId,
  loading
}: Props): React.JSX.Element => {
  const { windowWidth, windowHeight } = useWindowSize()
  const isMobile = windowWidth < BREAKPOINTS.md
  const isSmallMobile = isMobile && windowHeight < 750
  const dogData = useReactiveVar(dogsDataVar)

  const isSingleDog =
    dogs.length > 0 ? dogs.length === 1 : dogData?.dogs?.length === 1
  const onlyDog = isSingleDog && dogs ? dogs[0] : null

  const selectedOption = dailyAmountOptions.find(
    ({ id }) => id === chosenPlan?.id
  )

  const handleOptionChange = useCallback(
    ({ id }: DailyAmountOption) => setChosenPlanId(id),
    [setChosenPlanId]
  )

  return (
    <>
      <SectionWrapper margin={{ top: 0, bottom: 0 }}>
        <Fragment>
          <SectionWrapper
            {...(loading
              ? {}
              : {
                  headerTypography: {
                    text: 'edit_daily_amount.daily_calorie_guidance.option_selector.title',
                    namespace: 'account',
                    variant: 'textRegular16',
                    variables: {
                      dogName: upperFirst(onlyDog?.name) || '',
                      gramsPerDay:
                        currentPlan?.servingInformation?.gramsPerDay ?? 0,
                      count: dogs.length
                    },
                    margin: false
                  }
                })}
            headerClassName={STYLES.header}
            bgColour="brandYellow200"
            padding={
              isMobile
                ? { top: 24, right: 16, bottom: 24, left: 16 }
                : { top: 32, right: 32, bottom: 40, left: 32 }
            }
            borderRadius={16}
            margin={{ top: 0, bottom: 0 }}
          >
            <Fragment>
              {loading && (
                <div data-testid="daily-calorie-guidance-title-skeleton">
                  <SkeletonParagraph
                    align="left"
                    count={1}
                    shortLine={false}
                    width="43%"
                    height={17}
                    margin="0 0 .8rem"
                  />
                </div>
              )}
              <OptionSelector
                options={dailyAmountOptions}
                selectedOption={selectedOption}
                onOptionChange={handleOptionChange}
                loading={loading}
              />
              <SectionWrapper
                margin={{ top: isSmallMobile ? 24 : 32, bottom: 0 }}
              >
                <CalorieBar
                  lowerBound={onlyDog?.roundedDailyCaloriesLowerBoundary}
                  upperBound={onlyDog?.roundedDailyCaloriesUpperBoundary}
                  value={
                    chosenPlan?.servingInformation?.roundedCaloriesPerDay ??
                    currentPlan?.servingInformation?.roundedCaloriesPerDay ??
                    0
                  }
                  loading={loading}
                />
                {isSingleDog ? (
                  <div
                    className={STYLES.calorieRangeNote}
                    data-testId="daily-calorie-guidance-bar-key"
                  >
                    <div className={STYLES.calorieRangeKey} />
                    <Text
                      element="span"
                      colour="brandBlue500"
                      translate={false}
                      text={`=`}
                    />
                    {loading ? (
                      <SectionWrapper margin={{ top: 0, bottom: 0 }}>
                        <SkeletonParagraph
                          align="left"
                          count={1}
                          width="100%"
                          height={18}
                          margin="0 0 .8rem"
                        />
                      </SectionWrapper>
                    ) : (
                      <Text
                        namespace="account"
                        text="edit_daily_amount.daily_calorie_guidance.bar_key"
                        margin={false}
                        shouldScale={false}
                        variables={{
                          dogName: upperFirst(onlyDog?.name),
                          min: onlyDog?.roundedDailyCaloriesLowerBoundary,
                          max: onlyDog?.roundedDailyCaloriesUpperBoundary
                        }}
                      />
                    )}
                  </div>
                ) : (
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  <></>
                )}
              </SectionWrapper>
            </Fragment>
          </SectionWrapper>
          {!loading && (
            <SectionWrapper margin={{ top: 4, bottom: 0 }}>
              <MultipleDogsCallout dogs={dogs} chosenPlan={chosenPlan} />
            </SectionWrapper>
          )}
        </Fragment>
      </SectionWrapper>
      {isSingleDog && (
        <SectionWrapper margin={{ top: 0, bottom: 0 }}>
          <LargestPlanNotification chosenPlan={chosenPlan} />
        </SectionWrapper>
      )}
    </>
  )
}

export { DailyCalorieGuidance }
